import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from './supabase'

const IDLE_TIMEOUT = 900_000 // 15 minutes
const WARNING_BEFORE = 60_000 // 1 minute before logout

interface UseIdleLogoutOptions {
  enabled?: boolean
  timeoutMs?: number
  warningBeforeMs?: number
  redirectTo?: string
}

export function useIdleLogout({
  enabled = true,
  timeoutMs = IDLE_TIMEOUT,
  warningBeforeMs = WARNING_BEFORE,
  redirectTo = '/admin/login',
}: UseIdleLogoutOptions = {}) {
  const navigate = useNavigate()
  const [showWarning, setShowWarning] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
  }, [])

  const handleLogout = useCallback(async () => {
    console.log('[useIdleLogout] Logging out due to inactivity')
    clearTimers()
    setShowWarning(false)
    await supabase.auth.signOut()
    navigate(redirectTo, { replace: true })
  }, [clearTimers, navigate, redirectTo])

  const resetTimers = useCallback(() => {
    clearTimers()
    setShowWarning(false)

    // Set warning timer (fires 1 min before logout)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true)
    }, timeoutMs - warningBeforeMs)

    // Set logout timer
    idleTimerRef.current = setTimeout(() => {
      handleLogout()
    }, timeoutMs)
  }, [clearTimers, handleLogout, timeoutMs, warningBeforeMs])

  useEffect(() => {
    if (!enabled) return

    const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ]

    const onActivity = () => {
      resetTimers()
    }

    // Start timers
    resetTimers()

    // Listen for activity
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true })
    })

    return () => {
      clearTimers()
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity)
      })
    }
  }, [enabled, resetTimers, clearTimers])

  return { showWarning }
}
